import {
  CaptureUpdateAction,
  getSceneVersion,
  restoreElements,
  zoomToFitBounds,
  reconcileElements,
} from "@excalidraw/excalidraw";
import ConfirmDialog from "@excalidraw/excalidraw/components/ConfirmDialog";
import { ErrorDialog } from "@excalidraw/excalidraw/components/ErrorDialog";
import { APP_NAME, EVENT, KEYS, toBrandedType } from "@excalidraw/common";
import {
  IDLE_THRESHOLD,
  ACTIVE_THRESHOLD,
  UserIdleState,
  assertNever,
  isDevEnv,
  isTestEnv,
  preventUnload,
  resolvablePromise,
  throttleRAF,
} from "@excalidraw/common";
import { decryptData } from "@excalidraw/excalidraw/data/encryption";
import { getVisibleSceneBounds } from "@excalidraw/element";
import { newElementWith } from "@excalidraw/element";
import { isImageElement, isInitializedImageElement } from "@excalidraw/element";
import { t } from "@excalidraw/excalidraw/i18n";
import { withBatchedUpdates } from "@excalidraw/excalidraw/reactUtils";

import throttle from "lodash.throttle";
import { PureComponent } from "react";

import { bumpElementVersions } from "@excalidraw/excalidraw/data/restore";

import type {
  ReconciledExcalidrawElement,
  RemoteExcalidrawElement,
} from "@excalidraw/excalidraw/data/reconcile";
import type { ImportedDataState } from "@excalidraw/excalidraw/data/types";
import type {
  ExcalidrawElement,
  InitializedExcalidrawImageElement,
  OrderedExcalidrawElement,
} from "@excalidraw/element/types";
import type {
  ExcalidrawImperativeAPI,
  BinaryFiles,
  SocketId,
  Collaborator,
  Gesture,
  UserToFollow,
} from "@excalidraw/excalidraw/types";
import type { Mutable, ValueOf } from "@excalidraw/common/utility-types";

import { appJotaiStore, atom } from "../app-jotai";
import {
  CURSOR_SYNC_TIMEOUT,
  INITIAL_SCENE_UPDATE_TIMEOUT,
  LOAD_IMAGES_TIMEOUT,
  WS_SUBTYPES,
  SYNC_FULL_SCENE_INTERVAL_MS,
} from "../app_constants";
import {
  generateCollaborationLinkData,
  getCollaborationLink,
  getSyncableElements,
  isCollaborationLink,
} from "../data";
import { FileManager, updateStaleImageStatuses } from "../data/FileManager";
import { FileStatusStore } from "../data/fileStatusStore";
import { LocalData } from "../data/LocalData";
import {
  importUsernameFromLocalStorage,
  saveUsernameToLocalStorage,
} from "../data/localStorage";
import { resetBrowserStateVersions } from "../data/tabSync";
import { drawolaT } from "../storage/i18n";

import { collabErrorIndicatorAtom } from "./CollabError";
import { CollabFileRelay } from "./CollabFileRelay";
import {
  buildRoomWebSocketUrl,
  getCollabWorkerBaseUrl,
  joinRoomSession,
  reserveRoomSession,
  type CollabEntryMode,
} from "./collabSession";
import Portal from "./Portal";
import {
  RemoteCursorInterpolator,
  type CursorPoint,
} from "./RemoteCursorInterpolator";
import {
  isUserFollowRelayMessage,
  COLLAB_FILE_MAX_BYTES,
  parseCollabFileMessage,
  RETAIN_POLICIES,
  ROOM_CONTROL_CODES,
  decodeRelayPayload,
  type MemberListControlV1,
  type RetainPolicy,
  type RoomClosingControlV1,
  type UserFollowRelayMessageV1,
} from "./roomProtocol";
import { RoomTransport } from "./RoomTransport";

import type { SocketUpdateDataSource } from "../data";

export const collabAPIAtom = atom<CollabAPI | null>(null);
export const isCollaboratingAtom = atom(false);
export const isOfflineAtom = atom(false);
// Epoch ms the active room session expires at, or `null` when not
// collaborating. Read by `CollabSessionTimer` to show the countdown; set on
// a granted session (opened or joined) in `startCollaboration`, cleared in
// `destroySocketClient`.
export const collabSessionExpiresAtAtom = atom<number | null>(null);

/** The terms of the room this client is in, as the server reports them. */
export interface CollabRoomState {
  roomId: string;
  /** Whether this client opened the room or joined one somebody else opened. */
  entryMode: CollabEntryMode;
  /**
   * Whether people who joined by link may keep the drawing after the session
   * ends. Seeded from the HTTP grant so the UI has an answer before the socket
   * exists, then overwritten by every `member_list`, which is the
   * authoritative copy. Never the value this client asked for.
   */
  retainPolicy: RetainPolicy;
}

/**
 * The room this client is in, or `null` when not collaborating.
 *
 * Published as an atom so app-level code can act on the room's terms without
 * reaching into this class: the Studio document lifecycle
 * (`useCollabSessionDoc`) needs to know which room a draw belongs to, and the
 * share dialog needs to show the policy as a fact.
 */
export const collabRoomAtom = atom<CollabRoomState | null>(null);

/**
 * True while a joined room's scene has nowhere of its own to be saved yet.
 *
 * Set synchronously by `startCollaboration`, before the canvas is reset, and
 * cleared once `useCollabSessionDoc` has opened the session's own draw. The
 * multi-file store consults it on every write, so neither the empty canvas
 * left by the reset nor the room's incoming scene can be written into the draw
 * the user already had open. A jotai set is synchronous for readers, which is
 * what makes this safe without waiting for a React render.
 */
export const collabSessionDocPendingAtom = atom(false);

/**
 * The open "everybody else has left" question. Held as one object so the
 * dialog, its clock and its warning can never disagree: there is no state in
 * which a countdown is running without the dialog it belongs to.
 */
interface LastPeerPrompt {
  /** Whole seconds still on the clock, recomputed from `endsAtMs` each tick. */
  secondsLeft: number;
  /** When this question answers itself by leaving. */
  endsAtMs: number;
  /**
   * Whether leaving throws this client's copy of the drawing away, which is
   * true only for somebody who joined by link into a room the server reports
   * as `discard`. Captured when the question opens, from the room's own
   * reported terms rather than from anything this client asked for.
   */
  warnsDiscard: boolean;
}

interface CollabState {
  errorMessage: string | null;
  /** errors related to saving */
  dialogNotifiedErrors: Record<string, boolean>;
  username: string;
  activeRoomLink: string | null;
  /**
   * The question asked once everybody else has left, or `null` when it is not
   * up. The room belongs to nobody, so leaving is offered rather than forced,
   * but it is what happens if nothing is chosen: an abandoned room would
   * otherwise hold its reservation for the rest of its hour.
   */
  lastPeerPrompt: LastPeerPrompt | null;
}

/**
 * How long the joiner's one-off notice about the room's retain policy stays
 * up. Long enough to read a sentence without hunting for the close button,
 * which is also offered.
 */
const RETAIN_NOTICE_DURATION_MS = 10_000;

/**
 * How long the "everybody else has left" question waits before answering
 * itself by leaving. Long enough to read the question and decide, short
 * enough that a person who walked away does not hold the room's reservation
 * for the rest of its hour.
 */
const LAST_PEER_PROMPT_SECONDS = 60;

/** How often the countdown redraws. One second, because it counts seconds. */
const LAST_PEER_TICK_MS = 1_000;

/**
 * Drop a `#room=` fragment from the address bar.
 *
 * Every way out of a room goes through here, so a reload can never walk back
 * into a session that has ended or a join that was refused; leaving the hash
 * in place is what used to make a refusal repeat forever.
 *
 * Deliberately a no-op unless the current URL really is a collaboration link.
 * `stopCollaboration` also runs from App.tsx's `hashchange` handler, where the
 * user may have just pasted a `#json=` link that has to survive.
 */
const clearRoomHashFromUrl = (): void => {
  if (!isCollaborationLink(window.location.href)) {
    return;
  }
  window.history.replaceState({}, APP_NAME, window.location.origin);
};

export const activeRoomLinkAtom = atom<string | null>(null);
export const userToFollowAtom = atom<UserToFollow | null>(null);

type CollabInstance = InstanceType<typeof Collab>;

export interface CollabAPI {
  /** function so that we can access the latest value from stale callbacks */
  isCollaborating: () => boolean;
  onPointerUpdate: CollabInstance["onPointerUpdate"];
  startCollaboration: CollabInstance["startCollaboration"];
  stopCollaboration: CollabInstance["stopCollaboration"];
  syncElements: CollabInstance["syncElements"];
  fetchImageFiles: CollabInstance["fetchImageFiles"];
  setUsername: CollabInstance["setUsername"];
  getUsername: CollabInstance["getUsername"];
  getActiveRoomLink: CollabInstance["getActiveRoomLink"];
  setCollabError: CollabInstance["setErrorDialog"];
  setUserToFollow: CollabInstance["setUserToFollow"];
}

interface CollabProps {
  excalidrawAPI: ExcalidrawImperativeAPI;
}

// -----------------------------------------------------------------------------
// Wire validation. Everything below decrypts data another peer sent through
// the room relay, so it is treated as hostile: a decrypted JSON value is
// only ever used after it passes one of these checks, never cast directly.
// -----------------------------------------------------------------------------

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

type SceneSocketUpdateData = Exclude<
  ValueOf<SocketUpdateDataSource>,
  {
    type:
      | WS_SUBTYPES.FILE_PUSH
      | WS_SUBTYPES.FILE_REQUEST
      | WS_SUBTYPES.FILE_RESPONSE;
  }
>;

const KNOWN_SOCKET_UPDATE_TYPES = new Set<string>([
  WS_SUBTYPES.INVALID_RESPONSE,
  WS_SUBTYPES.INIT,
  WS_SUBTYPES.UPDATE,
  WS_SUBTYPES.MOUSE_LOCATION,
  WS_SUBTYPES.USER_VISIBLE_SCENE_BOUNDS,
  WS_SUBTYPES.IDLE_STATUS,
]);

/** Validates one decrypted relay payload against the known `SocketUpdateDataSource` shapes. */
const isKnownSocketUpdateData = (
  value: unknown,
): value is SceneSocketUpdateData => {
  if (!isRecord(value) || typeof value.type !== "string") {
    return false;
  }
  if (!KNOWN_SOCKET_UPDATE_TYPES.has(value.type)) {
    return false;
  }
  if (value.type === WS_SUBTYPES.INVALID_RESPONSE) {
    return true;
  }
  if (!isRecord(value.payload)) {
    return false;
  }
  switch (value.type) {
    case WS_SUBTYPES.INIT:
    case WS_SUBTYPES.UPDATE:
      return Array.isArray(value.payload.elements);
    case WS_SUBTYPES.MOUSE_LOCATION:
      return (
        isRecord(value.payload.pointer) &&
        typeof value.payload.username === "string"
      );
    case WS_SUBTYPES.USER_VISIBLE_SCENE_BOUNDS:
      return (
        isRecord(value.payload.sceneBounds) &&
        typeof value.payload.username === "string"
      );
    case WS_SUBTYPES.IDLE_STATUS:
      return (
        typeof value.payload.userState === "string" &&
        typeof value.payload.username === "string"
      );
    default:
      return false;
  }
};

class Collab extends PureComponent<CollabProps, CollabState> {
  portal: Portal;
  fileManager: FileManager;
  fileRelay: CollabFileRelay;
  excalidrawAPI: CollabProps["excalidrawAPI"];
  activeIntervalId: number | null;
  idleTimeoutId: number | null;

  private socketInitializationTimer?: number;
  /**
   * Smooths the 10 Hz remote pointer stream into per-frame motion. Owned here
   * because the collaborator map it feeds is owned here.
   */
  private cursorInterpolator: RemoteCursorInterpolator;
  private lastBroadcastedOrReceivedSceneVersion: number = -1;
  private collaborators = new Map<SocketId, Collaborator>();
  /** the socket ids of the users following the current user */
  private followedBy = new Set<SocketId>();
  /**
   * Who is in the room right now, derived from the relay's own membership
   * controls: `member_list` is authoritative and arrives after every change,
   * `peer_left` keeps this from lying in the gap before it does. There is no
   * polling anywhere: the room tells us.
   */
  private roomMembers = new Set<string>();
  /**
   * Whether anybody else has been in the room during this session. Without it
   * the very first `member_list` (which for the person opening the room lists
   * only themselves) would read as "everybody has left".
   */
  private hasSeenAnotherPeer = false;
  /** The interval driving the last-peer countdown, or null when none runs. */
  private lastPeerCountdownIntervalId: number | null = null;

  constructor(props: CollabProps) {
    super(props);
    this.state = {
      errorMessage: null,
      dialogNotifiedErrors: {},
      username: importUsernameFromLocalStorage() || "",
      activeRoomLink: null,
      lastPeerPrompt: null,
    };
    this.excalidrawAPI = props.excalidrawAPI;
    this.portal = new Portal(this);
    this.fileRelay = new CollabFileRelay({
      getCurrentClientId: () => this.portal.transport?.clientId ?? null,
      getFiles: this.getShareableCollabFiles,
      addFiles: (files) => this.excalidrawAPI.addFiles(files),
      sendMessage: (message) => this.portal.broadcastFileMessage(message),
      onFileError: (reason) => {
        const key =
          reason === "oversized"
            ? "collab.files.tooLarge"
            : reason === "malformed"
            ? "collab.files.malformed"
            : "collab.files.sendFailed";
        const limit = drawolaT("imageImport.sizeMB", {
          value: COLLAB_FILE_MAX_BYTES / 1024 / 1024,
        });
        this.excalidrawAPI.updateScene({
          appState: {
            errorMessage: drawolaT(key, { limit }),
          },
        });
      },
    });
    this.fileManager = new FileManager({
      onFileStatusChange: FileStatusStore.updateStatuses.bind(FileStatusStore),
      getFiles: (fileIds) => this.fileRelay.getFiles(fileIds),
      saveFiles: ({ addedFiles }) => this.fileRelay.saveFiles(addedFiles),
    });
    this.cursorInterpolator = new RemoteCursorInterpolator({
      onFrame: this.renderInterpolatedCursors,
    });
    this.activeIntervalId = null;
    this.idleTimeoutId = null;
  }

  private onUmmount: (() => void) | null = null;

  componentDidMount() {
    window.addEventListener(EVENT.BEFORE_UNLOAD, this.beforeUnload);
    window.addEventListener("online", this.onOfflineStatusToggle);
    window.addEventListener("offline", this.onOfflineStatusToggle);
    window.addEventListener(EVENT.UNLOAD, this.onUnload);

    const unsubOnUserFollow = this.excalidrawAPI.onUserFollow((payload) => {
      this.setUserToFollow(
        payload.action === "FOLLOW" ? payload.userToFollow : null,
      );
    });
    const throttledRelayUserViewportBounds = throttleRAF(
      this.relayVisibleSceneBounds,
    );
    const unsubOnScrollChange = this.excalidrawAPI.onScrollChange(() =>
      throttledRelayUserViewportBounds(),
    );
    this.onUmmount = () => {
      unsubOnUserFollow();
      unsubOnScrollChange();
    };

    this.onOfflineStatusToggle();

    const collabAPI: CollabAPI = {
      isCollaborating: this.isCollaborating,
      onPointerUpdate: this.onPointerUpdate,
      startCollaboration: this.startCollaboration,
      syncElements: this.syncElements,
      fetchImageFiles: this.fetchImageFiles,
      stopCollaboration: this.stopCollaboration,
      setUsername: this.setUsername,
      getUsername: this.getUsername,
      getActiveRoomLink: this.getActiveRoomLink,
      setCollabError: this.setErrorDialog,
      setUserToFollow: this.setUserToFollow,
    };

    appJotaiStore.set(collabAPIAtom, collabAPI);

    if (isTestEnv() || isDevEnv()) {
      window.collab = window.collab || ({} as Window["collab"]);
      Object.defineProperties(window, {
        collab: {
          configurable: true,
          value: this,
        },
      });
    }
  }

  onOfflineStatusToggle = () => {
    appJotaiStore.set(isOfflineAtom, !window.navigator.onLine);
  };

  componentWillUnmount() {
    window.removeEventListener("online", this.onOfflineStatusToggle);
    window.removeEventListener("offline", this.onOfflineStatusToggle);
    window.removeEventListener(EVENT.BEFORE_UNLOAD, this.beforeUnload);
    window.removeEventListener(EVENT.UNLOAD, this.onUnload);
    window.removeEventListener(EVENT.POINTER_MOVE, this.onPointerMove);
    window.removeEventListener(
      EVENT.VISIBILITY_CHANGE,
      this.onVisibilityChange,
    );
    if (this.activeIntervalId) {
      window.clearInterval(this.activeIntervalId);
      this.activeIntervalId = null;
    }
    if (this.idleTimeoutId) {
      window.clearTimeout(this.idleTimeoutId);
      this.idleTimeoutId = null;
    }
    this.releaseLastPeerPrompt();
    this.onUmmount?.();
    this.fileRelay.reset();
    this.cursorInterpolator.destroy();
  }

  isCollaborating = () => appJotaiStore.get(isCollaboratingAtom)!;

  private setIsCollaborating = (isCollaborating: boolean) => {
    appJotaiStore.set(isCollaboratingAtom, isCollaborating);
  };

  private setSessionExpiresAt = (expiresAt: number | null) => {
    appJotaiStore.set(collabSessionExpiresAtAtom, expiresAt);
  };

  /** Publishes the room's terms, or `null` once this client is out of it. */
  private setCurrentRoom = (room: CollabRoomState | null) => {
    appJotaiStore.set(collabRoomAtom, room);
  };

  /** The room's terms as last published, or `null` when not collaborating. */
  private getCurrentRoom = (): CollabRoomState | null =>
    appJotaiStore.get(collabRoomAtom) ?? null;

  private onUnload = () => {
    this.destroySocketClient({ isUnload: true });
  };

  private beforeUnload = withBatchedUpdates((event: BeforeUnloadEvent) => {
    const syncableElements = getSyncableElements(
      this.getSceneElementsIncludingDeleted(),
    );

    if (
      this.isCollaborating() &&
      this.fileManager.shouldPreventUnload(syncableElements)
    ) {
      if (import.meta.env.VITE_APP_DISABLE_PREVENT_UNLOAD !== "true") {
        preventUnload(event);
      } else {
        console.warn(
          "preventing unload disabled (VITE_APP_DISABLE_PREVENT_UNLOAD)",
        );
      }
    }
  });

  stopCollaboration = (keepRemoteState = true) => {
    this.queueBroadcastAllElements.cancel();
    this.loadImageFiles.cancel();
    this.resetErrorIndicator(true);

    if (this.portal.transport && this.fallbackInitializationHandler) {
      this.portal.transport.off(
        "reconnectFailed",
        this.fallbackInitializationHandler,
      );
    }

    if (!keepRemoteState) {
      LocalData.fileStorage.reset();
      clearRoomHashFromUrl();
      this.destroySocketClient();
    } else if (window.confirm(t("alerts.collabStopOverridePrompt"))) {
      // hack to ensure that we prefer we disregard any new browser state
      // that could have been saved in other tabs while we were collaborating
      resetBrowserStateVersions();

      window.history.pushState({}, APP_NAME, window.location.origin);
      this.destroySocketClient();

      LocalData.fileStorage.reset();

      const elements = this.excalidrawAPI
        .getSceneElementsIncludingDeleted()
        .map((element) => {
          if (isImageElement(element) && element.status === "saved") {
            return newElementWith(element, { status: "pending" });
          }
          return element;
        });

      this.excalidrawAPI.updateScene({
        elements,
        captureUpdate: CaptureUpdateAction.NEVER,
      });
    }
  };

  private destroySocketClient = (opts?: { isUnload: boolean }) => {
    this.lastBroadcastedOrReceivedSceneVersion = -1;
    // Unconditional, and before anything else: the session is over either
    // way, so its clock must stop even on the unload path that skips the
    // state reset below.
    this.releaseLastPeerPrompt();
    // No client-side release call here on purpose: the client has no
    // authoritative count of accepted inbound messages, so it must not be
    // the one to settle usage. Only the room itself does that (see
    // `RoomDO.releaseQuota` in `room-do.ts`), and an abandoned reservation
    // that never reaches a room is reclaimed lazily by `QuotaLedger`.
    this.portal.close();
    this.fileRelay.reset();
    this.fileManager.reset();
    this.cursorInterpolator.reset();
    this.followedBy = new Set();
    this.roomMembers = new Set();
    this.hasSeenAnotherPeer = false;
    if (!opts?.isUnload) {
      this.setIsCollaborating(false);
      this.setSessionExpiresAt(null);
      this.setActiveRoomLink(null);
      // Last: whoever owns the session's draw acts on this going away, and it
      // needs the rest of the teardown to have happened first.
      this.setCurrentRoom(null);
      appJotaiStore.set(collabSessionDocPendingAtom, false);
      this.setState({ lastPeerPrompt: null });
      appJotaiStore.set(userToFollowAtom, null);
      this.collaborators = new Map();
      this.excalidrawAPI.updateScene({
        collaborators: this.collaborators,
      });
      LocalData.resumeSave("collaboration");
    }
  };

  private fetchImageFiles = async (opts: {
    elements: readonly ExcalidrawElement[];
    /**
     * Indicates whether to fetch files that are errored or pending and older
     * than 10 seconds.
     *
     * Use this as a mechanism to fetch files which may be ok but for some
     * reason their status was not updated correctly.
     */
    forceFetchFiles?: boolean;
  }) => {
    const unfetchedImages = opts.elements
      .filter((element) => {
        return (
          isInitializedImageElement(element) &&
          !this.fileManager.isFileTracked(element.fileId) &&
          !element.isDeleted &&
          (opts.forceFetchFiles
            ? element.status !== "pending" ||
              Date.now() - element.updated > 10000
            : element.status === "saved")
        );
      })
      .map((element) => (element as InitializedExcalidrawImageElement).fileId);

    return await this.fileManager.getFiles(unfetchedImages);
  };

  // Returns `unknown` (rather than casting straight to a known payload
  // shape) because the wire is hostile: `isKnownSocketUpdateData` below is
  // what actually validates it before the switch below is allowed to touch
  // it.
  private decryptPayload = async (
    iv: Uint8Array<ArrayBuffer>,
    encryptedData: ArrayBuffer,
    decryptionKey: string,
  ): Promise<unknown> => {
    try {
      const decrypted = await decryptData(iv, encryptedData, decryptionKey);

      const decodedData = new TextDecoder("utf-8").decode(
        new Uint8Array(decrypted),
      );
      return JSON.parse(decodedData);
    } catch (error) {
      window.alert(t("alerts.decryptFailed"));
      console.error(error);
      return {
        type: WS_SUBTYPES.INVALID_RESPONSE,
      };
    }
  };

  private fallbackInitializationHandler: null | (() => any) = null;

  startCollaboration = async (
    existingRoomLinkData: null | { roomId: string; roomKey: string },
    options?: {
      /**
       * What the person opening the room asks for. Ignored when joining one,
       * and never displayed: the room's answer is what the UI shows.
       */
      retainPolicy?: RetainPolicy;
    },
  ) => {
    if (!this.state.username) {
      import("@excalidraw/random-username").then(({ getRandomUsername }) => {
        const username = getRandomUsername();
        this.setUsername(username);
      });
    }

    if (this.portal.transport) {
      return null;
    }

    // An existing `#room=` link means somebody else already opened (and paid
    // for) this room, so this peer must take the join path. Naming the choice
    // keeps the two Worker transactions from being confused again: a joiner
    // that reserved its own session got a capability the relay refused as
    // `room_session_mismatch`, which is why only one person could ever
    // collaborate.
    const entryMode: CollabEntryMode = existingRoomLinkData ? "join" : "open";

    let roomId;
    let roomKey;

    if (existingRoomLinkData) {
      ({ roomId, roomKey } = existingRoomLinkData);
      // Shut the multi-file store's write gate before anything touches the
      // canvas. The join path resets the scene a few lines below and then
      // fills it with the room's drawing; either of those written into the
      // draw the user already had open would destroy their work.
      appJotaiStore.set(collabSessionDocPendingAtom, true);
    } else {
      ({ roomId, roomKey } = await generateCollaborationLinkData());
      window.history.pushState(
        {},
        APP_NAME,
        getCollaborationLink({ roomId, roomKey }),
      );
    }

    // TODO: `ImportedDataState` type here seems abused
    const scenePromise = resolvablePromise<
      | (ImportedDataState & { elements: readonly OrderedExcalidrawElement[] })
      | null
    >();

    this.setIsCollaborating(true);
    LocalData.pauseSave("collaboration");

    // A room token has to be obtained before we can even construct the
    // transport: the WebSocket upgrade carries it on the URL and `RoomDO`
    // rejects the connection outright without one.
    const workerBaseUrl = getCollabWorkerBaseUrl();
    const session =
      entryMode === "join"
        ? await joinRoomSession(workerBaseUrl, roomId)
        : await reserveRoomSession(
            workerBaseUrl,
            roomId,
            options?.retainPolicy,
          );
    if (!session.granted) {
      console.error(
        `Collaboration room ${entryMode} was refused: ${session.reason}`,
      );
      // The countdown must not start for a session this peer never entered:
      // showing 60 minutes and then a "lost connection" error is what made
      // the old refusal path so misleading.
      this.setErrorDialog(drawolaT(`collab.session.reason.${session.reason}`));
      this.setIsCollaborating(false);
      // A refusal that left `#room=` in place meant every reload hit the same
      // error again, with no way out but editing the address bar.
      clearRoomHashFromUrl();
      this.setCurrentRoom(null);
      appJotaiStore.set(collabSessionDocPendingAtom, false);
      LocalData.resumeSave("collaboration");
      return null;
    }
    // For a joiner this is the room's real remaining time, not a fresh full
    // session: the Worker signs the running session's own expiry.
    this.setSessionExpiresAt(session.expiresAt);
    // The grant's policy is the server's answer, not this client's guess. It
    // is only the seed: every `member_list` overwrites it.
    this.setCurrentRoom({
      roomId,
      entryMode,
      retainPolicy: session.retainPolicy,
    });

    const fallbackInitializationHandler = () => {
      this.initializeRoom();
      scenePromise.resolve(null);
    };
    this.fallbackInitializationHandler = fallbackInitializationHandler;

    // Tracks whether the transport has completed its handshake before, so
    // an `open` after a mid-session drop is recognized as a reconnect
    // (reconcile) rather than the initial connect (which has its own
    // scene-handoff flow further below).
    let hasConnectedBefore = false;
    // The first `member_list` control this session sees with only ourselves
    // in it is the "first in room" signal; every later one is an ordinary
    // membership update.
    let hasSeenInitialMembership = false;

    try {
      const transport = new RoomTransport(
        buildRoomWebSocketUrl(workerBaseUrl, roomId, session.roomToken),
      );
      this.portal.transport = this.portal.open(transport, roomId, roomKey);

      transport.on("open", () => {
        this.resetErrorIndicator();
        if (hasConnectedBefore && this.portal.socketInitialized) {
          this.portal.broadcastScene(
            WS_SUBTYPES.UPDATE,
            this.excalidrawAPI.getSceneElementsIncludingDeleted(),
            /* syncAll */ true,
          );
        }
        hasConnectedBefore = true;
      });
      transport.on("reconnecting", () => {
        this.setErrorIndicator(drawolaT("collab.connection.reconnecting"));
      });
      transport.on("reconnectFailed", () => {
        this.setErrorIndicator(drawolaT("collab.connection.lost"));
      });
      transport.on("reconnectFailed", fallbackInitializationHandler);

      transport.connect();
    } catch (error: any) {
      console.error(error);
      this.setErrorDialog(error.message);
      // Nothing is going to open the session's draw now, so the store's write
      // gate has to be handed back or every later save would be dropped.
      appJotaiStore.set(collabSessionDocPendingAtom, false);
      return null;
    }

    if (existingRoomLinkData) {
      // when joining existing room, don't merge it with current scene data
      this.excalidrawAPI.resetScene();
    } else {
      const elements = this.excalidrawAPI.getSceneElements().map((element) => {
        if (isImageElement(element) && element.status === "saved") {
          return newElementWith(element, { status: "pending" });
        }
        return element;
      });
      // remove deleted elements from elements array to ensure we don't
      // expose potentially sensitive user data in case user manually deletes
      // existing elements (or clears scene), which would otherwise be persisted
      // to database even if deleted before creating the room.
      this.excalidrawAPI.updateScene({
        elements,
        captureUpdate: CaptureUpdateAction.NEVER,
      });
    }

    // fallback in case you're not alone in the room but still don't receive
    // initial SCENE_INIT message
    this.socketInitializationTimer = window.setTimeout(
      fallbackInitializationHandler,
      INITIAL_SCENE_UPDATE_TIMEOUT,
    );

    const transport = this.portal.transport!;

    // Replaces the old `client-broadcast` socket.io listener: every relay
    // envelope (durable or volatile) arrives through this one event now.
    transport.on("relay", async (envelope) => {
      if (!this.portal.roomKey) {
        return;
      }

      const { iv, ciphertext } = decodeRelayPayload(envelope.payload);
      const decryptedData = await this.decryptPayload(
        iv,
        ciphertext,
        this.portal.roomKey,
      );
      // Authoritative: the relay derives this from the accepted socket, so
      // unlike a payload's own self-reported `socketId` field, a sender
      // cannot forge it.
      const senderClientId = toBrandedType<SocketId>(envelope.senderClientId);

      if (isUserFollowRelayMessage(decryptedData)) {
        this.handleFollowRelayMessage(senderClientId, decryptedData);
        return;
      }

      const fileMessage = parseCollabFileMessage(decryptedData);
      if (fileMessage) {
        await this.fileRelay.handleMessage(senderClientId, fileMessage);
        return;
      }

      if (!isKnownSocketUpdateData(decryptedData)) {
        console.error(
          "Ignoring malformed collaboration relay message",
          decryptedData,
        );
        return;
      }

      switch (decryptedData.type) {
        case WS_SUBTYPES.INVALID_RESPONSE:
          return;
        case WS_SUBTYPES.INIT: {
          if (!this.portal.socketInitialized) {
            this.initializeRoom();
            const remoteElements = toBrandedType<
              readonly RemoteExcalidrawElement[]
            >(decryptedData.payload.elements);
            const reconciledElements = this._reconcileElements(remoteElements);
            this.handleRemoteSceneUpdate(reconciledElements);
            // resolves scenePromise if it hasn't already resolved via the
            // alone-in-room fallback below
            scenePromise.resolve({
              elements: reconciledElements,
              scrollToContent: true,
            });
          }
          break;
        }
        case WS_SUBTYPES.UPDATE:
          this.handleRemoteSceneUpdate(
            this._reconcileElements(
              toBrandedType<readonly RemoteExcalidrawElement[]>(
                decryptedData.payload.elements,
              ),
            ),
          );
          break;
        case WS_SUBTYPES.MOUSE_LOCATION: {
          const { pointer, button, username, selectedElementIds } =
            decryptedData.payload;

          this.updateCollaboratorPointer(senderClientId, {
            pointer,
            button,
            selectedElementIds,
            username,
          });

          break;
        }

        case WS_SUBTYPES.USER_VISIBLE_SCENE_BOUNDS: {
          const { sceneBounds } = decryptedData.payload;

          const userToFollow = appJotaiStore.get(userToFollowAtom);

          // we're not following the user
          // (shouldn't happen, but could be late message or bug upstream)
          if (userToFollow?.socketId !== senderClientId) {
            console.warn(
              `receiving remote client's (from ${senderClientId}) viewport bounds even though we're not subscribed to it!`,
            );
            return;
          }

          // cross-follow case, ignore updates in this case
          if (this.followedBy.has(userToFollow.socketId)) {
            return;
          }

          const appState = this.excalidrawAPI.getAppState();

          this.excalidrawAPI.updateScene({
            appState: zoomToFitBounds({
              appState,
              bounds: sceneBounds,
              fit: "contain",
            }).appState,
          });

          break;
        }

        case WS_SUBTYPES.IDLE_STATUS: {
          const { userState, username } = decryptedData.payload;
          this.updateCollaborator(senderClientId, {
            userState,
            username,
          });
          break;
        }

        default: {
          assertNever(decryptedData, null);
        }
      }
    });

    // Replaces the old `first-in-room` and `user-follow-room-change`
    // socket.io listeners: both used to be computed by the server, but the
    // new relay only relays, so this app derives them itself from the
    // `member_list` / `peer_left` control codes instead.
    transport.on("control", (control) => {
      if (control.code === ROOM_CONTROL_CODES.MEMBER_LIST) {
        const isInitialMembership = !hasSeenInitialMembership;
        hasSeenInitialMembership = true;
        this.handleMemberList(control, { isInitialMembership });
        if (isInitialMembership && control.members.length === 1) {
          // We were alone when we joined: equivalent to the old
          // "first-in-room". No peer will hand us the initial scene, so
          // just mark the room ready and keep whatever is already on the
          // canvas (see `initializeRoom`).
          this.initializeRoom();
          scenePromise.resolve(null);
        }
        return;
      }
      if (control.code === ROOM_CONTROL_CODES.PEER_LEFT) {
        this.handlePeerLeft(control.clientId);
        return;
      }
      if (control.code === ROOM_CONTROL_CODES.ROOM_CLOSING) {
        this.handleRoomClosing(control.reason);
      }
    });

    this.initializeIdleDetector();

    this.setActiveRoomLink(window.location.href);

    return scenePromise;
  };

  /**
   * Applies one relayed follow/unfollow message, ignoring it unless we are
   * the addressed target. Replaces the old server-computed
   * `user-follow-room-change` broadcast: the dumb relay cannot compute "who
   * follows me" itself, so every client derives its own `followedBy` set
   * from these messages instead.
   */
  private handleFollowRelayMessage = (
    senderClientId: SocketId,
    message: UserFollowRelayMessageV1,
  ) => {
    if (message.targetClientId !== this.portal.transport?.clientId) {
      return;
    }
    if (message.action === "FOLLOW") {
      this.followedBy.add(senderClientId);
    } else {
      this.followedBy.delete(senderClientId);
    }
  };

  /**
   * Applies one `member_list`: the room's own account of who is present and of
   * the terms it was opened with.
   *
   * Two things come out of it. The retain policy is refreshed, because this
   * copy is the authoritative one and it is the only one a peer that arrived
   * after the opener left would ever see. And the membership is compared with
   * what it was: dropping to just this client, after somebody else has been
   * here, means everybody has left. That is offered as a question rather than
   * acted on, because the room belongs to nobody and the user may well be
   * waiting for someone to come back.
   */
  private handleMemberList = (
    control: MemberListControlV1,
    opts: { isInitialMembership: boolean },
  ) => {
    const room = this.getCurrentRoom();
    if (room && room.retainPolicy !== control.retainPolicy) {
      this.setCurrentRoom({ ...room, retainPolicy: control.retainPolicy });
    }
    if (opts.isInitialMembership && room?.entryMode === "join") {
      // Told once, as they arrive, and from the authoritative copy rather than
      // from the grant.
      this.excalidrawAPI.setToast({
        message: drawolaT(
          control.retainPolicy === RETAIN_POLICIES.RETAIN
            ? "collab.retain.joinerRetain"
            : "collab.retain.joinerDiscard",
        ),
        duration: RETAIN_NOTICE_DURATION_MS,
        closable: true,
      });
    }

    this.roomMembers = new Set(control.members);
    this.checkForLastPeer();
  };

  /**
   * Applies one `peer_left`. The room sends `member_list` after every
   * membership change too, so this is not the only account of the departure;
   * it is the immediate one, and taking it means the question below is asked
   * as soon as the room says somebody went, rather than one message later.
   */
  private handlePeerLeft = (clientId: string) => {
    this.followedBy.delete(toBrandedType<SocketId>(clientId));
    this.roomMembers.delete(clientId);
    this.checkForLastPeer();
  };

  /**
   * Offers to end the session once this client is the only one left.
   *
   * Asked once per emptying: if somebody comes back and leaves again the
   * question is worth asking a second time, but the two messages that report
   * one departure must not ask it twice.
   */
  private checkForLastPeer = () => {
    if (this.roomMembers.size > 1) {
      this.hasSeenAnotherPeer = true;
      return;
    }
    if (!this.hasSeenAnotherPeer) {
      return;
    }
    this.hasSeenAnotherPeer = false;
    this.openLastPeerPrompt();
  };

  /**
   * Puts the question up with its own clock running, and its Escape guard in
   * place. Both start here so neither can exist without the dialog.
   */
  private openLastPeerPrompt = (): void => {
    this.releaseLastPeerPrompt();
    const room = this.getCurrentRoom();
    this.setState({
      lastPeerPrompt: {
        secondsLeft: LAST_PEER_PROMPT_SECONDS,
        endsAtMs: Date.now() + LAST_PEER_PROMPT_SECONDS * 1_000,
        // Read from the room's reported terms, never guessed: only somebody
        // who came in by link can lose a drawing by leaving, and only when
        // the room the server describes does not keep it.
        warnsDiscard:
          room !== null &&
          room.entryMode === "join" &&
          room.retainPolicy === RETAIN_POLICIES.DISCARD,
      },
    });
    window.addEventListener(EVENT.KEYDOWN, this.swallowLastPeerEscape, true);
    this.lastPeerCountdownIntervalId = window.setInterval(
      this.tickLastPeerCountdown,
      LAST_PEER_TICK_MS,
    );
  };

  /**
   * Counts the clock down, and leaves when it runs out.
   *
   * Derived from `endsAtMs` rather than by subtracting one per tick, so a
   * throttled background tab still leaves after sixty real seconds instead of
   * after sixty however-long-they-took ticks.
   */
  private tickLastPeerCountdown = (): void => {
    const prompt = this.state.lastPeerPrompt;
    if (prompt === null) {
      this.releaseLastPeerPrompt();
      return;
    }
    const secondsLeft = Math.max(
      0,
      Math.ceil((prompt.endsAtMs - Date.now()) / 1_000),
    );
    if (secondsLeft === 0) {
      this.endSessionFromLastPeerPrompt();
      return;
    }
    this.setState({ lastPeerPrompt: { ...prompt, secondsLeft } });
  };

  /**
   * Swallows Escape while the question is up.
   *
   * Upstream's `Modal` turns Escape into `onCloseRequest`, which `ConfirmDialog`
   * wires to `onCancel`, so the key would silently pick "stay" for the user.
   * This question has to be answered on purpose, and upstream offers no prop
   * to disable that (`packages/` is upstream territory here), so the key is
   * stopped in the capture phase on `window`, before React's delegated
   * listener on the modal's portal container can ever see it.
   */
  private swallowLastPeerEscape = (event: KeyboardEvent): void => {
    if (event.key === KEYS.ESCAPE) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  /**
   * Releases everything the question owns, its clock and its Escape guard, so
   * neither can outlive the dialog. Safe to call when nothing is running,
   * which is why every exit path calls it unconditionally.
   */
  private releaseLastPeerPrompt = (): void => {
    if (this.lastPeerCountdownIntervalId !== null) {
      window.clearInterval(this.lastPeerCountdownIntervalId);
      this.lastPeerCountdownIntervalId = null;
    }
    window.removeEventListener(EVENT.KEYDOWN, this.swallowLastPeerEscape, true);
  };

  /** Takes the question down and stops its clock, whatever answered it. */
  private closeLastPeerPrompt = (): void => {
    this.releaseLastPeerPrompt();
    this.setState({ lastPeerPrompt: null });
  };

  /**
   * Surfaces the room's shutdown reason and tears down the local
   * collaboration state.
   *
   * The room now ends for two different causes under this one control message:
   * the 60 minute cap, and everybody having left for longer than the grace
   * period. Deliberately a different message from a dropped connection: this
   * one is final and the link is dead, while a dropped connection can still be
   * rejoined by reloading, so the two must not read the same.
   */
  private handleRoomClosing = (_reason: RoomClosingControlV1["reason"]) => {
    this.setErrorDialog(drawolaT("collab.roomClosed.sessionExpired"));
    clearRoomHashFromUrl();
    this.destroySocketClient();
  };

  /**
   * Marks the room ready to send/receive once we know no peer is going to
   * hand us the initial scene: called when we are alone in the room, when
   * the reconnect-fallback timer fires, or when a peer's `SCENE_INIT`
   * message arrives. There is nothing to fetch from storage any more
   * (Firebase removed): the initial scene always travels peer to peer via
   * `Portal.open`'s `PEER_JOINED` broadcast, so being alone just means we
   * keep whatever is already on the canvas.
   */
  private initializeRoom = () => {
    clearTimeout(this.socketInitializationTimer!);
    if (this.portal.transport && this.fallbackInitializationHandler) {
      this.portal.transport.off(
        "reconnectFailed",
        this.fallbackInitializationHandler,
      );
    }
    this.portal.socketInitialized = true;
  };

  private _reconcileElements = (
    remoteElements: readonly RemoteExcalidrawElement[],
  ): ReconciledExcalidrawElement[] => {
    const appState = this.excalidrawAPI.getAppState();

    const existingElements = this.getSceneElementsIncludingDeleted();

    // NOTE ideally we restore _after_ reconciliation but we can't do that
    // as we'd regenerate even elements such as appState.newElement which would
    // break the state
    remoteElements = restoreElements(remoteElements, existingElements);

    let reconciledElements = reconcileElements(
      existingElements,
      remoteElements,
      appState,
    );

    reconciledElements = bumpElementVersions(
      reconciledElements,
      existingElements,
    );

    // Avoid broadcasting to the rest of the collaborators the scene
    // we just received!
    // Note: this needs to be set before updating the scene as it
    // synchronously calls render.
    this.setLastBroadcastedOrReceivedSceneVersion(
      getSceneVersion(reconciledElements),
    );

    return reconciledElements;
  };

  private loadImageFiles = throttle(async () => {
    const { loadedFiles, erroredFiles } = await this.fetchImageFiles({
      elements: this.excalidrawAPI.getSceneElementsIncludingDeleted(),
    });

    this.excalidrawAPI.addFiles(loadedFiles);

    updateStaleImageStatuses({
      excalidrawAPI: this.excalidrawAPI,
      erroredFiles,
      elements: this.excalidrawAPI.getSceneElementsIncludingDeleted(),
    });
  }, LOAD_IMAGES_TIMEOUT);

  private handleRemoteSceneUpdate = (
    elements: ReconciledExcalidrawElement[],
  ) => {
    this.excalidrawAPI.updateScene({
      elements,
      captureUpdate: CaptureUpdateAction.NEVER,
    });

    this.loadImageFiles();
  };

  private onPointerMove = () => {
    if (this.idleTimeoutId) {
      window.clearTimeout(this.idleTimeoutId);
      this.idleTimeoutId = null;
    }

    this.idleTimeoutId = window.setTimeout(this.reportIdle, IDLE_THRESHOLD);

    if (!this.activeIntervalId) {
      this.activeIntervalId = window.setInterval(
        this.reportActive,
        ACTIVE_THRESHOLD,
      );
    }
  };

  private onVisibilityChange = () => {
    if (document.hidden) {
      if (this.idleTimeoutId) {
        window.clearTimeout(this.idleTimeoutId);
        this.idleTimeoutId = null;
      }
      if (this.activeIntervalId) {
        window.clearInterval(this.activeIntervalId);
        this.activeIntervalId = null;
      }
      this.onIdleStateChange(UserIdleState.AWAY);
    } else {
      this.idleTimeoutId = window.setTimeout(this.reportIdle, IDLE_THRESHOLD);
      this.activeIntervalId = window.setInterval(
        this.reportActive,
        ACTIVE_THRESHOLD,
      );
      this.onIdleStateChange(UserIdleState.ACTIVE);
    }
  };

  private reportIdle = () => {
    this.onIdleStateChange(UserIdleState.IDLE);
    if (this.activeIntervalId) {
      window.clearInterval(this.activeIntervalId);
      this.activeIntervalId = null;
    }
  };

  private reportActive = () => {
    this.onIdleStateChange(UserIdleState.ACTIVE);
  };

  private initializeIdleDetector = () => {
    document.addEventListener(EVENT.POINTER_MOVE, this.onPointerMove);
    document.addEventListener(EVENT.VISIBILITY_CHANGE, this.onVisibilityChange);
  };

  setCollaborators(sockets: SocketId[]) {
    const collaborators: InstanceType<typeof Collab>["collaborators"] =
      new Map();
    for (const socketId of sockets) {
      const isCurrentUser = socketId === this.portal.transport?.clientId;
      collaborators.set(
        socketId,
        Object.assign(
          // we never receive our own broadcasts, so we need to seed
          // our own collaborator entry with the local username
          isCurrentUser ? { username: this.state.username } : {},
          this.collaborators.get(socketId),
          { isCurrentUser },
        ),
      );
    }
    this.collaborators = collaborators;
    // A peer that left, or that reconnected under a new client id, must not
    // keep a cursor gliding towards a position nobody will ever update again.
    this.cursorInterpolator.retain(collaborators.keys());
    this.excalidrawAPI.updateScene({ collaborators });

    // unfollow if the followed user left the room
    const userToFollow = appJotaiStore.get(userToFollowAtom);
    if (userToFollow && !collaborators.has(userToFollow.socketId)) {
      this.setUserToFollow(null);
    }
  }

  updateCollaborator = (
    socketId: SocketId,
    updates: Partial<Collaborator>,
    options?: {
      /**
       * Records the change without pushing it to the editor, for a caller that
       * knows the cursor animation frame is about to push it anyway. Saves the
       * editor a full render per message per peer.
       */
      deferRender?: boolean;
    },
  ) => {
    const isCurrentUser = socketId === this.portal.transport?.clientId;
    const collaborators = new Map(this.collaborators);
    const user: Mutable<Collaborator> = Object.assign(
      // we never receive our own broadcasts, so we need to seed
      // our own collaborator entry with the local username
      isCurrentUser ? { username: this.state.username } : {},
      collaborators.get(socketId),
      updates,
      { isCurrentUser },
    );
    collaborators.set(socketId, user);
    this.collaborators = collaborators;

    if (options?.deferRender) {
      return;
    }

    this.excalidrawAPI.updateScene({
      collaborators,
    });
  };

  /**
   * Applies one remote pointer message. The reported position only becomes the
   * interpolation target; the collaborator entry gets the position that belongs
   * on screen this instant, so a 100 ms send interval still renders as
   * continuous motion instead of ten visible steps a second.
   */
  private updateCollaboratorPointer = (
    socketId: SocketId,
    updates: Omit<
      SocketUpdateDataSource["MOUSE_LOCATION"]["payload"],
      "socketId"
    >,
  ) => {
    const drawnAt = this.cursorInterpolator.track(socketId, updates.pointer);
    this.updateCollaborator(
      socketId,
      {
        ...updates,
        pointer: { ...updates.pointer, x: drawnAt.x, y: drawnAt.y },
      },
      { deferRender: this.cursorInterpolator.isAnimating },
    );
  };

  /**
   * Pushes one animation frame's worth of interpolated cursor positions into
   * the editor. Every peer that moved travels in a single `updateScene` call,
   * so the frame cost is flat in the number of collaborators rather than one
   * editor render per peer per message.
   */
  private renderInterpolatedCursors = (
    positions: ReadonlyMap<SocketId, CursorPoint>,
  ) => {
    const collaborators = new Map(this.collaborators);
    let hasUpdate = false;
    for (const [socketId, point] of positions) {
      const collaborator = collaborators.get(socketId);
      if (!collaborator?.pointer) {
        continue;
      }
      collaborators.set(socketId, {
        ...collaborator,
        pointer: { ...collaborator.pointer, x: point.x, y: point.y },
      });
      hasUpdate = true;
    }
    if (!hasUpdate) {
      return;
    }
    this.collaborators = collaborators;
    this.excalidrawAPI.updateScene({ collaborators });
  };

  public setLastBroadcastedOrReceivedSceneVersion = (version: number) => {
    this.lastBroadcastedOrReceivedSceneVersion = version;
  };

  public getLastBroadcastedOrReceivedSceneVersion = () => {
    return this.lastBroadcastedOrReceivedSceneVersion;
  };

  public getSceneElementsIncludingDeleted = () => {
    return this.excalidrawAPI.getSceneElementsIncludingDeleted();
  };

  /** Limits peer file responses to images referenced by the current live scene. */
  private getShareableCollabFiles = (): BinaryFiles => {
    const files = this.excalidrawAPI.getFiles();
    return this.excalidrawAPI
      .getSceneElements()
      .reduce<BinaryFiles>((shareableFiles, element) => {
        if (isInitializedImageElement(element) && files[element.fileId]) {
          shareableFiles[element.fileId] = files[element.fileId];
        }
        return shareableFiles;
      }, {});
  };

  onPointerUpdate = throttle(
    (payload: {
      pointer: SocketUpdateDataSource["MOUSE_LOCATION"]["payload"]["pointer"];
      button: SocketUpdateDataSource["MOUSE_LOCATION"]["payload"]["button"];
      pointersMap: Gesture["pointers"];
    }) => {
      payload.pointersMap.size < 2 &&
        this.portal.transport &&
        this.portal.broadcastMouseLocation(payload);
    },
    CURSOR_SYNC_TIMEOUT,
  );

  relayVisibleSceneBounds = (props?: { force: boolean }) => {
    if (this.portal.transport && (this.followedBy.size > 0 || props?.force)) {
      this.portal.broadcastVisibleSceneBounds({
        sceneBounds: getVisibleSceneBounds(this.excalidrawAPI.getAppState()),
      });
    }
  };

  onIdleStateChange = (userState: UserIdleState) => {
    this.portal.broadcastIdleChange(userState);
  };

  broadcastElements = (elements: readonly OrderedExcalidrawElement[]) => {
    if (
      getSceneVersion(elements) >
      this.getLastBroadcastedOrReceivedSceneVersion()
    ) {
      this.portal.broadcastScene(WS_SUBTYPES.UPDATE, elements, false);
      this.lastBroadcastedOrReceivedSceneVersion = getSceneVersion(elements);
      this.queueBroadcastAllElements();
    }
  };

  syncElements = (elements: readonly OrderedExcalidrawElement[]) => {
    this.broadcastElements(elements);
  };

  queueBroadcastAllElements = throttle(() => {
    this.portal.broadcastScene(
      WS_SUBTYPES.UPDATE,
      this.excalidrawAPI.getSceneElementsIncludingDeleted(),
      true,
    );
    const currentVersion = this.getLastBroadcastedOrReceivedSceneVersion();
    const newVersion = Math.max(
      currentVersion,
      getSceneVersion(this.getSceneElementsIncludingDeleted()),
    );
    this.setLastBroadcastedOrReceivedSceneVersion(newVersion);
  }, SYNC_FULL_SCENE_INTERVAL_MS);

  setUserToFollow = (userToFollow: UserToFollow | null) => {
    const prev = appJotaiStore.get(userToFollowAtom) ?? null;

    if (prev?.socketId !== userToFollow?.socketId && this.portal.transport) {
      // leave the previous user's follow room before joining the next one
      if (prev) {
        this.portal.broadcastUserFollowed({
          userToFollow: prev,
          action: "UNFOLLOW",
        });
      }
      if (userToFollow) {
        this.portal.broadcastUserFollowed({
          userToFollow,
          action: "FOLLOW",
        });
      }
    }

    appJotaiStore.set(userToFollowAtom, userToFollow);
  };

  setUsername = (username: string) => {
    this.setState({ username });
    saveUsernameToLocalStorage(username);

    // keep our own collaborator entry in sync
    const socketId = this.portal.transport?.clientId as SocketId | undefined;
    if (socketId && this.collaborators.has(socketId)) {
      this.updateCollaborator(socketId, { username });
    }
  };

  getUsername = () => this.state.username;

  setActiveRoomLink = (activeRoomLink: string | null) => {
    this.setState({ activeRoomLink });
    appJotaiStore.set(activeRoomLinkAtom, activeRoomLink);
  };

  getActiveRoomLink = () => this.state.activeRoomLink;

  setErrorIndicator = (errorMessage: string | null) => {
    appJotaiStore.set(collabErrorIndicatorAtom, {
      message: errorMessage,
      nonce: Date.now(),
    });
  };

  resetErrorIndicator = (resetDialogNotifiedErrors = false) => {
    appJotaiStore.set(collabErrorIndicatorAtom, { message: null, nonce: 0 });
    if (resetDialogNotifiedErrors) {
      this.setState({
        dialogNotifiedErrors: {},
      });
    }
  };

  setErrorDialog = (errorMessage: string | null) => {
    this.setState({
      errorMessage,
    });
  };

  /**
   * Ends the session from the "everybody else has left" question. Also what
   * the countdown does when nobody answers, so the two cannot diverge.
   */
  private endSessionFromLastPeerPrompt = () => {
    this.closeLastPeerPrompt();
    // `false` skips upstream's "this overwrites your local drawing" prompt:
    // that question is about upstream's single stored scene, and asking it on
    // top of the one the user has just answered would be noise.
    this.stopCollaboration(false);
  };

  /** Keeps the session open, in case somebody comes back. */
  private dismissLastPeerPrompt = () => {
    this.closeLastPeerPrompt();
  };

  render() {
    const { errorMessage, lastPeerPrompt } = this.state;

    return (
      <>
        {errorMessage != null && (
          <ErrorDialog onClose={() => this.setErrorDialog(null)}>
            {errorMessage}
          </ErrorDialog>
        )}
        {lastPeerPrompt !== null && (
          <ConfirmDialog
            title={drawolaT("collab.lastPeer.title")}
            confirmText={drawolaT("collab.lastPeer.end")}
            cancelText={drawolaT("collab.lastPeer.stay")}
            onConfirm={this.endSessionFromLastPeerPrompt}
            onCancel={this.dismissLastPeerPrompt}
            // This one has to be answered, so every incidental way out of an
            // upstream dialog is shut: the backdrop click here, Escape in
            // `swallowLastPeerEscape`, and the phone-only close button by the
            // CSS gate this class name exists for (see `index.scss`).
            closeOnClickOutside={false}
            className="drawola-last-peer-dialog"
          >
            <p>{drawolaT("collab.lastPeer.body")}</p>
            {lastPeerPrompt.warnsDiscard && (
              <p>{drawolaT("collab.lastPeer.discardWarning")}</p>
            )}
            <p>
              {drawolaT("collab.lastPeer.countdown", {
                seconds: lastPeerPrompt.secondsLeft,
              })}
            </p>
          </ConfirmDialog>
        )}
      </>
    );
  }
}

declare global {
  interface Window {
    collab: InstanceType<typeof Collab>;
  }
}

if (isTestEnv() || isDevEnv()) {
  window.collab = window.collab || ({} as Window["collab"]);
}

export default Collab;

export type TCollabClass = Collab;
